import { format, parse, isValid } from 'date-fns';
import { removeTimeZone } from '@utmost.co/utmost-design-system';
import { logger } from '../../logger';

type ParsedDate<T extends string | Date | undefined> = T extends undefined ? undefined : Date;

export function formatDate<T>(
  // new Date('yyyy-MM-dd') will parse the date as utc into local time (most browsers)
  date?: T,
  outputFormat: string = 'MMM dd, yyyy',
  inputFormat: string = 'yyyy-MM-dd'
): string | undefined {
  if (!date) {
    return undefined;
  }
  return typeof date === 'string'
    ? format(parse(date, inputFormat, new Date()), outputFormat)
    : format(date as unknown as Date, outputFormat);
}

export function parseDate<T extends string | Date | undefined>(
  date: T,
  inputFormat: string = 'yyyy-MM-dd',
  throwError = true
): ParsedDate<T> {
  if (!date) {
    return undefined as T extends undefined ? T : never;
  }
  const result = typeof date === 'string' ? parse(date, inputFormat, new Date()) : date;
  if (throwError && result instanceof Date && Number.isNaN(result.getTime())) {
    // fallback
    return new Date(date as Date) as T extends undefined ? T : never;
  }
  return result as T extends undefined ? T : never;
}

export type DateOffsetTemplate = string; // 1h, 1d, -14h, -28d etc. (-)[int][h|d|y]
export type OffsetUnit = 'h' | 'd' | 'y';

export enum PointInTime {
  START = 'start',
  NOW = 'now',
  END = 'end'
}

const millisecondsMultiplier = new Map<OffsetUnit, number>([
  ['h', 3600000],
  ['d', 86400000],
  ['y', 31536000000]
]);

export function pointInTimeToDate(
  date?: PointInTime | string | Date | undefined | null,
  offset: number | DateOffsetTemplate = 0,
  isMin: boolean = true
): Date | undefined | string {
  if (!date) {
    return undefined;
  }

  // Legacy is when date validation is from now and the offset is in MS - this is unlikely to be used (legacy code?)
  const isLegacy = date === PointInTime.NOW && typeof offset === 'number' && offset !== 0;

  // contextDate is the base date we're going to validate against. This is dependent on the type of parameters being used.
  let contextDate: string | Date | PointInTime;

  if (isLegacy) contextDate = PointInTime.NOW;
  else if (date !== PointInTime.NOW) contextDate = date;
  else if (isMin) contextDate = PointInTime.START;
  else contextDate = PointInTime.END;

  const offsetInMS = parsePeriodToMS(offset);

  let hasHours = false;
  if (typeof offset === 'string' && offset.includes('h')) {
    hasHours = true;
  }

  const now = new Date();
  const utcDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  try {
    switch (contextDate) {
      case PointInTime.START:
        const toReturn = new Date(new Date(utcDate.getTime() + offsetInMS));
        return hasHours ? toReturn : removeTimeZone(toReturn);
      case PointInTime.NOW:
        return new Date(new Date(utcDate.getTime() + offsetInMS));
      case PointInTime.END:
        return new Date(new Date().setHours(23, 59, 59, 999) + offsetInMS);
      default: {
        const dateToValidate = typeof date === 'string' ? Date.parse(date) : date;
        if (isValid(new Date(dateToValidate)) || isValid(dateToValidate)) {
          let targetDate: Date;
          if (typeof date === 'string') {
            targetDate = parseDate(date);
          } else {
            targetDate = date;
          }
          const result = getDateWithTimezoneOffset(targetDate, offsetInMS);
          return parseDate(result);
        }
        return undefined;
      }
    }
  } catch (e) {
    logger.error(e);
    throw e;
  }
}

const getDateWithTimezoneOffset = (targetDate: Date, millisecondsToOffset: number): Date => {
  const offSetTime = new Date(targetDate.getTime() + millisecondsToOffset);
  if (offSetTime.getTimezoneOffset() === targetDate.getTimezoneOffset()) {
    return offSetTime;
  }
  return new Date(offSetTime.getTime() + offSetTime.getTimezoneOffset() * 60000);
};

// Only exported to test.
export function parsePeriodToMS(period?: number | DateOffsetTemplate): number {
  if (!period) {
    return 0;
  }
  if (typeof period === 'number') {
    return period;
  }
  let targetPeriod = period;
  const lastChar = period.charAt(period.length - 1).toLowerCase();
  if ((period.length > 0 && period.length < 2) || !(['d', 'h', 'y'] as OffsetUnit[]).includes(lastChar as OffsetUnit)) {
    targetPeriod = `${period}d`;
  }
  const isMinus = targetPeriod.charAt(0) === '-';
  const offset = isMinus ? 1 : 0;
  const numberRange = targetPeriod.length - offset - 1;
  const unit = targetPeriod.charAt(targetPeriod.length - 1).toLowerCase();
  const number = parseInt(targetPeriod.substring(offset, offset + numberRange), 10);
  const result = number * millisecondsMultiplier.get(unit as OffsetUnit)!;
  return isMinus ? -result : result;
}

export function millisecondsTo(milliseconds: number, unit: OffsetUnit): number {
  return Math.round(milliseconds / millisecondsMultiplier.get(unit)!);
}

export function isYearOffset(value?: string | number): value is string {
  return typeof value === 'string' && value.includes('y');
}

export function periodToNumber(value: string | undefined, unit: OffsetUnit): number {
  return Math.abs(millisecondsTo(parsePeriodToMS(value), unit));
}
